import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Agent } from "@/types/agent";
import { defaultAgents } from "@/config/defaultAgents";
import { supabase } from "@/integrations/supabase/client";
import { mapAgentWithIcon } from "@/utils/agentUtils";
import { useApiKeys } from "./useApiKeys";
import { useAgentUpdates } from "./useAgentUpdates";

export const useAgents = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const { toast } = useToast();
  const {
    tempApiKeys,
    setTempApiKeys,
    tempOpenaiApiKeys,
    setTempOpenaiApiKeys,
    tempGeminiApiKeys,
    setTempGeminiApiKeys,
    showApiKeys,
    setShowApiKeys,
    showOpenaiApiKeys,
    setShowOpenaiApiKeys,
    showGeminiApiKeys,
    setShowGeminiApiKeys,
    updateAgentApiKey,
    updateAgentOpenaiApiKey,
    updateAgentGeminiApiKey,
  } = useApiKeys();
  
  const { updateAgentPrompt, updatePreferredApi } = useAgentUpdates();

  useEffect(() => {
    console.log("Initializing agents...");
    initializeAgents();
  }, []);

  const initializeAgents = async () => {
    try {
      console.log("Fetching existing agents...");
      const { data: existingAgents, error } = await supabase
        .from('ai_agents')
        .select('*')
        .order('id', { ascending: true });

      if (error) {
        console.error('Error fetching agents:', error);
        toast({
          title: "Erro ao carregar agentes",
          description: "Houve um erro ao carregar os agentes do banco de dados.",
          variant: "destructive",
        });
        return;
      }

      if (!existingAgents || existingAgents.length === 0) {
        console.log("No agents found, inserting default agents...");
        await insertDefaultAgents();
      } else {
        console.log("Mapping existing agents...", existingAgents);
        const mappedAgents = mapExistingAgents(existingAgents);
        setAgents(mappedAgents);
        initializeTempKeys(mappedAgents);
      }
    } catch (error) {
      console.error('Error in initializeAgents:', error);
      toast({
        title: "Erro na inicialização",
        description: "Houve um erro ao inicializar os agentes.",
        variant: "destructive",
      });
    }
  };

  const insertDefaultAgents = async () => {
    const defaultMappedAgents = defaultAgents.map(agent => ({
      name: agent.name,
      description: agent.description,
      icon_name: agent.iconName,
      model: agent.model,
      api_key: agent.apiKey,
      openai_api_key: agent.openaiApiKey,
      gemini_api_key: agent.geminiApiKey,
      avatar: agent.avatar,
      prompt: agent.prompt,
      preferred_api: agent.preferredApi,
    }));

    for (const agent of defaultMappedAgents) {
      const { error: insertError } = await supabase
        .from('ai_agents')
        .insert([agent]);

      if (insertError) {
        console.error('Error inserting default agent:', insertError);
      }
    }

    const { data: newAgents, error: fetchError } = await supabase
      .from('ai_agents')
      .select('*')
      .order('id', { ascending: true });

    if (fetchError) {
      console.error('Error fetching new agents:', fetchError);
      return;
    }

    if (newAgents) {
      const mappedAgents = mapExistingAgents(newAgents);
      setAgents(mappedAgents);
      initializeTempKeys(mappedAgents);
    }
  };

  const mapExistingAgents = (agentsData: any[]) => {
    return agentsData.map(agent => mapAgentWithIcon({
      id: agent.id,
      name: agent.name,
      description: agent.description,
      iconName: agent.icon_name,
      model: agent.model,
      apiKey: agent.api_key || '',
      openaiApiKey: agent.openai_api_key || '',
      geminiApiKey: agent.gemini_api_key || '',
      avatar: agent.avatar,
      prompt: agent.prompt,
      preferredApi: agent.preferred_api as "mistral" | "openai" | "gemini",
    }));
  };

  const initializeTempKeys = (agents: Agent[]) => {
    const initialTempKeys = agents.reduce((acc: { [key: number]: string }, agent: Agent) => {
      acc[agent.id] = agent.apiKey || '';
      return acc;
    }, {});
    
    const initialTempOpenaiKeys = agents.reduce((acc: { [key: number]: string }, agent: Agent) => {
      acc[agent.id] = agent.openaiApiKey || '';
      return acc;
    }, {});

    const initialTempGeminiKeys = agents.reduce((acc: { [key: number]: string }, agent: Agent) => {
      acc[agent.id] = agent.geminiApiKey || '';
      return acc;
    }, {});
    
    setTempApiKeys(initialTempKeys);
    setTempOpenaiApiKeys(initialTempOpenaiKeys);
    setTempGeminiApiKeys(initialTempGeminiKeys);
  };

  return {
    agents,
    setAgents,
    tempApiKeys,
    setTempApiKeys,
    tempOpenaiApiKeys,
    setTempOpenaiApiKeys,
    tempGeminiApiKeys,
    setTempGeminiApiKeys,
    showApiKeys,
    setShowApiKeys,
    showOpenaiApiKeys,
    setShowOpenaiApiKeys,
    showGeminiApiKeys,
    setShowGeminiApiKeys,
    updateAgentPrompt,
    updateAgentApiKey,
    updateAgentOpenaiApiKey,
    updateAgentGeminiApiKey,
    updatePreferredApi
  };
};